import React, { useEffect, useState } from "react";
import api from "../../../api/axios";
import Swal from "sweetalert2";

export default function PayrollFormulas() {
  const [formulas, setFormulas] = useState([]);
  const [itemCodes, setItemCodes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [usingSample, setUsingSample] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const generateCode = (name) => {
    if (!name) return "";
    return name.trim().replace(/\s+/g, "_").toUpperCase();
  };

  const blankForm = {
    name: "",
    expression: "",
    description: "",
    code: "",
  };

  const [form, setForm] = useState(blankForm);

  /* ---------- TEST MODAL STATE ---------- */
  const [testModal, setTestModal] = useState({
    open: false,
    expression: "",
    vars: {},
    result: null,
  });

  useEffect(() => {
    loadFormulas();
    loadItemCodes();
  }, []);

  /* ======================================================
      LOAD PAYROLL ITEM CODES
  ====================================================== */
  const loadItemCodes = async () => {
    try {
      const res = await api.get("hrms/prms/items");
      const codes = res.data.data.data.map((i) => i.code);
      setItemCodes(codes);
    } catch (err) {
      console.warn("Using sample payroll item codes");
      setItemCodes(["BASIC", "HRA", "ALLOW", "DA", "PF"]);
    }
  };

  /* ======================================================
      LOAD FORMULAS
  ====================================================== */
  const loadFormulas = async () => {
    setLoading(true);

    try {
      const res = await api.get("hrms/prms/formulas");
      setFormulas(res.data.data.data);
      setUsingSample(false);
    } catch (err) {
      console.warn("API failed → using sample formulas");

      const sample = [
        {
          id: 1,
          name: "Gross Salary",
          expression: "BASIC + HRA + ALLOW",
          description: "Sum of total earnings",
        },
        {
          id: 2,
          name: "Net Salary",
          expression: "GROSS - PF",
          description: "Final take-home",
        },
      ];

      setFormulas(sample);
      setUsingSample(true);
    }

    setLoading(false);
  };

  /* ======================================================
      EXPRESSION INPUT VALIDATION
  ====================================================== */
  const handleExpressionChange = (e) => {
    const value = e.target.value.toUpperCase();

    const allowed = /^[A-Z0-9_+\-*/() ]*$/;
    if (!allowed.test(value)) return;

    setForm({ ...form, expression: value });
  };

  const filteredSuggestions = (() => {
    const lastToken = form.expression.split(/[\s+\-*/()]+/).pop() || "";
    return itemCodes.filter((code) => code.startsWith(lastToken));
  })();

  /* ======================================================
      SAVE FORMULA
  ====================================================== */
  const saveFormula = async () => {
    const payload = {
      ...form,
      code: generateCode(form.name),
    };

    try {
      if (editId) {
        await api.put(`hrms/prms/formulas/${editId}`, payload);
        Swal.fire("Updated!", "Formula updated", "success");
      } else {
        await api.post("hrms/prms/formulas", payload);
        Swal.fire("Saved!", "Formula added", "success");
      }

      setShowAddForm(false);
      setEditId(null);
      setForm(blankForm);
      loadFormulas();
    } catch (err) {
      Swal.fire("Sample Mode", "Saved locally (API not ready)", "info");

      if (editId) {
        setFormulas((prev) =>
          prev.map((f) => (f.id === editId ? { ...f, ...form } : f))
        );
      } else {
        setFormulas((prev) => [...prev, { id: prev.length + 1, ...form }]);
      }

      setShowAddForm(false);
      setEditId(null);
      setForm(blankForm);
    }
  };

  /* ======================================================
      EDIT FORMULA
  ====================================================== */
  const startEdit = (item) => {
    setShowAddForm(false);
    setEditId(item.id);
    setForm({
      name: item.name,
      expression: item.expression,
      description: item.description,
    });
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditId(null);
    setForm(blankForm);
  };

  /* ======================================================
      EXTRACT VARIABLES
  ====================================================== */
  const extractVariables = (expr) => {
    return [...new Set(expr.match(/[A-Z_]+/g) || [])];
  };

  /* ======================================================
      OPEN TEST MODAL
  ====================================================== */
  const testFormula = (item) => {
    const vars = extractVariables(item.expression);
    const initial = {};
    vars.forEach((v) => (initial[v] = ""));

    setTestModal({
      open: true,
      expression: item.expression,
      vars: initial,
      result: null,
    });
  };

  /* ======================================================
      LOCAL CALCULATION (UI ONLY)
  ====================================================== */
  const calculateLocal = () => {
    let expr = testModal.expression;

    Object.entries(testModal.vars).forEach(([key, val]) => {
      const num = parseFloat(val || 0);
      expr = expr.replaceAll(key, num);
    });

    try {
      // eslint-disable-next-line no-new-func
      const result = Function(`"use strict"; return (${expr})`)();
      setTestModal({ ...testModal, result });
    } catch (e) {
      setTestModal({ ...testModal, result: "Invalid Expression" });
    }
  };

  /* ======================================================
      LOADING
  ====================================================== */
  if (loading) {
    return <div className="text-center fw-bold fs-4 p-5">Loading...</div>;
  }

  /* ======================================================
      MAIN RETURN
  ====================================================== */
  return (
    <div style={{ background: "#eef6fd", minHeight: "100vh" }}>
      <div className="container py-4">
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold">Payroll Formulas</h3>

          <button
            className="btn btn-primary"
            onClick={() => {
              cancelForm();
              setShowAddForm(true);
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Formula
          </button>
        </div>

        {usingSample && (
          <span className="badge bg-warning text-dark mb-3">
            Using Sample Data
          </span>
        )}

        {/* ADD/EDIT FORM */}
        {showAddForm && renderForm("Add Formula")}

        {/* LIST */}
        <div className="bg-white border rounded shadow-sm p-4">
          <h5 className="fw-bold mb-4">Manage Formulas</h5>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Expression</th>
                  <th>Description</th>
                  <th>Test</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {formulas.map((item) => (
                  <tr key={item.id}>
                    {editId === item.id ? (
                      <td colSpan={5}>{renderForm("Edit Formula", true)}</td>
                    ) : (
                      <>
                        <td>{item.name}</td>

                        <td>
                          <span className="badge bg-info-subtle text-primary px-3 py-2">
                            {item.expression}
                          </span>
                        </td>

                        <td className="text-muted">{item.description}</td>

                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => testFormula(item)}
                          >
                            Test
                          </button>
                        </td>

                        <td>
                          <div className="dropdown">
                            <i
                              className="bi bi-three-dots-vertical"
                              role="button"
                              data-bs-toggle="dropdown"
                            ></i>

                            <ul className="dropdown-menu">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => startEdit(item)}
                                >
                                  Edit
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item text-danger">
                                  Delete
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ======================================================
          TEST MODAL UI
      ====================================================== */}
      {testModal.open && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
        >
          <div className="bg-white p-4 rounded shadow" style={{ width: "400px" }}>
            <h5 className="fw-bold mb-3">Test Formula</h5>

            <p className="text-muted">
              <span className="fw-semibold">Expression:</span>{" "}
              <span className="badge bg-info text-dark px-3 py-2">
                {testModal.expression}
              </span>
            </p>

            {/* INPUT FIELDS */}
            {Object.keys(testModal.vars).map((key) => (
              <div className="mb-2" key={key}>
                <label className="form-label">{key}</label>
                <input
                  className="form-control"
                  type="number"
                  value={testModal.vars[key]}
                  onChange={(e) =>
                    setTestModal({
                      ...testModal,
                      vars: { ...testModal.vars, [key]: e.target.value },
                    })
                  }
                />
              </div>
            ))}

            {/* RESULT */}
            {testModal.result !== null && (
              <div className="alert alert-info text-center fw-bold">
                Result: {testModal.result}
              </div>
            )}

            <div className="d-flex justify-content-end mt-3 gap-2">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  setTestModal({
                    open: false,
                    expression: "",
                    vars: {},
                    result: null,
                  })
                }
              >
                Close
              </button>

              <button className="btn btn-primary" onClick={calculateLocal}>
                Calculate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ======================================================
      INLINE FORM RENDER
  ====================================================== */
  function renderForm(title, isEdit = false) {
    return (
      <div className="bg-white border rounded shadow-sm p-4 mb-3">
        <h5 className="fw-bold mb-3">{title}</h5>

        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Name</label>
            <input
              className="form-control"
              name="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Gross Salary"
            />
          </div>

          <div className="col-md-6 position-relative">
            <label className="form-label fw-semibold">Expression</label>

            <input
              className="form-control"
              name="expression"
              value={form.expression}
              onChange={handleExpressionChange}
              placeholder="BASIC + HRA + ALLOW"
              autoComplete="off"
            />

            {filteredSuggestions.length > 0 &&
              form.expression &&
              !form.expression.endsWith(" ") && (
                <ul
                  className="list-group position-absolute mt-1 w-100 shadow-sm"
                  style={{ zIndex: 20 }}
                >
                  {filteredSuggestions.map((code) => (
                    <li
                      key={code}
                      className="list-group-item list-group-item-action"
                      onClick={() => {
                        const parts =
                          form.expression.split(/[\s+\-*/()]+/);
                        parts.pop();
                        const newExpr = [...parts, code].join(" ");
                        setForm({ ...form, expression: newExpr + " " });
                      }}
                    >
                      {code}
                    </li>
                  ))}
                </ul>
              )}
          </div>

          <div className="col-md-12">
            <label className="form-label fw-semibold">Description</label>
            <textarea
              className="form-control"
              name="description"
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Short description"
            ></textarea>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn btn-secondary" onClick={cancelForm}>
            Cancel
          </button>

          <button className="btn btn-primary" onClick={saveFormula}>
            {isEdit ? "Update Formula" : "Save Formula"}
          </button>
        </div>
      </div>
    );
  }
}
